import { useEffect, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { ProgressBar, BackButton, PracticeLogo } from "@mwi/ui";

import { RootState } from "store";
import useTitle from "hooks/useTitle";
import { login, clearAuth } from "slices/auth";
import {
  reset,
  setPatientOptions,
  setVerifyMethod,
  setVerifyMethods,
  updateFormState,
} from "slices/registration";

import RegistrationDetails from "./steps/details";
import RegistrationAddress from "./steps/address";
import RegistrationPractice from "./steps/practice";
import RegistrationPreviousPractice from "./steps/previous_practice";
import RegistrationPatientVerify from "./steps/patient_verify";
import RegistrationBailout from "./steps/bailout";
import RegistrationCodeVerify from "./steps/code_verify";
import RegistrationSetPassword from "./steps/set_password";
import RegistrationCodeSelect from "./steps/code_select";
import RegistrationCreatePatient from "./steps/create_patient";

enum Step {
  DETAILS = 1,
  ADDRESS = 2,
  VET_SITE = 3,
  PREVIOUS_PRACTICE = 4,
  VERIFICATION = 5,
  SET_PASSWORD = 6,
  CREATE_PATIENT = 7,
}

const initialStates = {
  details: {
    first_name: "",
    last_name: "",
    phone: "",
    terms: undefined,
  },
  address: {
    name_no: "",
    line_1: "",
    line_2: "",
    city: "",
    county: "",
    postcode: "",
  },
  previous_practice: {
    previous_practice: undefined,
    previous_practice_name: undefined,
    previous_practice_address_postcode: undefined,
    previous_practice_address_line_1: undefined,
    previous_practice_address_line_2: undefined,
    previous_practice_address_town: undefined,
    previous_practice_address_county: undefined,
    previous_practice_phone: undefined,
  },
  vet_site: {
    vet_site: "",
  },
};

const RegisterPage = () => {
  useTitle("Register");

  const dispatch = useDispatch();

  const [step, setStep] = useState(Step.DETAILS);

  const {
    isAuthenticated,
    user,
    themeLogo,
    practiceName,
    patientOptions,
    verifyMethods,
    verifyMethod,
    bailed,
    formState,
    flow,
  } = useSelector((state: RootState) => ({
    isAuthenticated: state.auth.authenticated,
    user: state.auth.user,
    themeLogo: state.practice.theme.logoUrl,
    practiceName: state.practice.name,
    patientOptions: state.registration.patientOptions,
    verifyMethods: state.registration.verifyMethods,
    verifyMethod: state.registration.verifyMethod,
    bailed: state.registration.bailed,
    formState: state.registration.formState,
    flow: state.registration.flow,
  }));

  useEffect(() => {
    // All flows must enter their details to accept terms, except verified accounts
    if (bailed) return; // Skip if bailed

    if (user?.verified_at && !user.onboarding_completed_at) {
      setStep(Step.SET_PASSWORD);
      return;
    } // Step 6: Set password
    if (user?.onboarding_completed_at && user.patient_required) {
      setStep(Step.CREATE_PATIENT);
      return;
    } // Step 7: Create patient(s)

    if (formState?.email && !formState.phone) {
      setStep(Step.DETAILS);
      return;
    } // Step 1: Details

    if (
      flow === "match" &&
      (patientOptions || verifyMethods?.length || verifyMethod) &&
      !user?.verified_at
    ) {
      setStep(Step.VERIFICATION);
      return;
    } // If details are complete but user isn't verified, skip to Verification (step 4)

    // Otherwise, continue with registration flow
    if (flow === "register") {
      if (!formState.postcode) {
        setStep(Step.ADDRESS);
        return;
      } // Step 2: Address
      if (!formState.vet_site) {
        setStep(Step.VET_SITE);
        return;
      } // Step 3: Vet Site
      if (formState.previous_practice == undefined || !verifyMethods?.length) {
        setStep(Step.PREVIOUS_PRACTICE);
        return;
      } // Step 4: Previous practice details - captures previous practice but also fires the registration, along with getting the verifyMethods
      if (verifyMethods?.length && !user?.verified_at) {
        setStep(Step.VERIFICATION);
        return;
      } // Step 5: Select Verification
    }
  }, [bailed, user, verifyMethods, verifyMethod, formState]);

  const onBack = () => {
    if (step === Step.DETAILS) {
      dispatch(reset());
      if (isAuthenticated) dispatch(clearAuth());
    } else if (step === Step.ADDRESS) {
      dispatch(
        updateFormState({ ...initialStates.address, ...initialStates.details }),
      );
    } else if (step === Step.VET_SITE) {
      dispatch(
        updateFormState({
          ...initialStates.vet_site,
          ...initialStates.address,
        }),
      );
    } else if (step === Step.PREVIOUS_PRACTICE) {
      dispatch(
        updateFormState({
          ...initialStates.previous_practice,
          ...initialStates.vet_site,
        }),
      );
    } else if (step === Step.VERIFICATION) {
      if (patientOptions && !verifyMethods?.length) {
        dispatch(setPatientOptions(undefined));

        if (flow === "match") {
          dispatch(
            updateFormState({
              verified_patient: "",
              ...initialStates.details,
              ...initialStates.address,
              ...initialStates.vet_site,
              ...initialStates.previous_practice,
            }),
          );
        } else {
          dispatch(
            updateFormState({
              verified_patient: "",
              ...initialStates.previous_practice,
            }),
          );
        }
      } else if (verifyMethods?.length && !verifyMethod) {
        dispatch(setVerifyMethods(undefined));
        dispatch(clearAuth());

        if (flow === "match") {
          patientOptions
            ? dispatch(updateFormState({ verified_patient: "" }))
            : dispatch(
                updateFormState({
                  verified_patient: "",
                  ...initialStates.details,
                  ...initialStates.address,
                  ...initialStates.vet_site,
                  ...initialStates.previous_practice,
                }),
              );
        } else {
          dispatch(
            updateFormState({
              verified_patient: "",
              ...initialStates.previous_practice,
            }),
          );
        }
      } else if (step === Step.VERIFICATION && verifyMethod) {
        dispatch(setVerifyMethod(undefined));
        dispatch(login({ user: { ...user!, verified_at: undefined } }));
      } else {
        dispatch(reset());
      }
    } else {
      dispatch(reset());
    }
  };

  if (!formState.email && !isAuthenticated) {
    return <Navigate to="/" />;
  }
  if (
    isAuthenticated &&
    user?.onboarding_completed_at &&
    !user?.patient_required
  ) {
    return <Navigate to="/home" />;
  }

  const NavItem = ({
    children,
    disabled = false,
    active = false,
  }: {
    children: string;
    disabled?: boolean;
    active?: boolean;
  }) => (
    <li
      className={classNames("py-2 pl-8 font-semibold", {
        "text-disabled-grey": disabled,
        "text-primary border-l-4 border-primary": active,
      })}
    >
      {children}
    </li>
  );

  return (
    <div className="p-4 tablet:p-0">
      <div
        className={classNames(
          "flex items-center tablet:border-b border-element-grey tablet:pb-6",
          { "pb-4": isAuthenticated },
        )}
      >
        {!bailed &&
          step !== Step.SET_PASSWORD &&
          step !== Step.CREATE_PATIENT && (
            <BackButton onClick={onBack} className="tablet:hidden" />
          )}
        <PracticeLogo
          logo={themeLogo}
          name={practiceName}
          className="hidden tablet:block"
        />
      </div>

      <div className="flex h-full pt-12 tablet:gap-8 desktop:gap-52 tablet:pt-20">
        <ul className="flex-col hidden gap-4 tablet:flex whitespace-nowrap">
          <NavItem active={step === Step.DETAILS}>
            Step 1: Contact Details
          </NavItem>
          <NavItem
            active={step === Step.ADDRESS}
            disabled={step < Step.ADDRESS}
          >
            Step 2: Your Address
          </NavItem>
          <NavItem
            active={step === Step.VET_SITE}
            disabled={step < Step.VET_SITE}
          >
            Step 3: Preferred Practice
          </NavItem>
          <NavItem
            active={step === Step.PREVIOUS_PRACTICE}
            disabled={step < Step.PREVIOUS_PRACTICE}
          >
            Step 4: Previous Practice
          </NavItem>
          <NavItem
            active={step === Step.VERIFICATION}
            disabled={step < Step.VERIFICATION}
          >
            Step 5: Verify Details
          </NavItem>
          <NavItem
            active={step === Step.SET_PASSWORD}
            disabled={step < Step.SET_PASSWORD}
          >
            Step 6: Set Password
          </NavItem>
          {user?.patient_required && (
            <NavItem
              active={step === Step.CREATE_PATIENT}
              disabled={step < Step.CREATE_PATIENT}
            >
              Step 7: Register your pet
            </NavItem>
          )}
        </ul>

        <div className="flex flex-col w-full">
          {!bailed &&
            step !== Step.SET_PASSWORD &&
            step !== Step.CREATE_PATIENT && (
              <BackButton
                onClick={onBack}
                className="hidden mb-6 tablet:flex"
              />
            )}

          {!bailed && (flow === "register" || user?.patient_required) && (
            <div className="mb-10 tablet:mb-16">
              <div className="flex-grow">
                <ProgressBar
                  max={
                    user?.patient_required
                      ? Step.CREATE_PATIENT
                      : Step.SET_PASSWORD
                  }
                  value={step}
                />
                <p className="mt-4 text-sm font-semibold tablet:text-lg">
                  Step {step} of{" "}
                  {user?.patient_required
                    ? Step.CREATE_PATIENT
                    : Step.SET_PASSWORD}
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col flex-grow col-span-4 tablet:col-span-4">
            {!bailed && step === Step.DETAILS && <RegistrationDetails />}
            {!bailed && step === Step.ADDRESS && <RegistrationAddress />}
            {!bailed && step === Step.VET_SITE && <RegistrationPractice />}
            {!bailed && step === Step.PREVIOUS_PRACTICE && (
              <RegistrationPreviousPractice />
            )}
            {!bailed &&
              step === Step.VERIFICATION &&
              patientOptions &&
              !formState.verified_patient && <RegistrationPatientVerify />}
            {!bailed &&
              step === Step.VERIFICATION &&
              (patientOptions ? formState.verified_patient : true) &&
              verifyMethods?.length &&
              !verifyMethod && <RegistrationCodeSelect />}
            {!bailed && step === Step.VERIFICATION && verifyMethod && (
              <RegistrationCodeVerify />
            )}
            {!bailed && step === Step.SET_PASSWORD && (
              <RegistrationSetPassword />
            )}
            {!bailed && step === Step.CREATE_PATIENT && (
              <RegistrationCreatePatient />
            )}
            {bailed && <RegistrationBailout />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
